// Bespoke marks for the academy.
//
// Every glyph is drawn on the same 24-unit grid with a single stroke weight, so
// the set reads as one alphabet rather than a pile of borrowed icons. They are
// deliberately geometric and slightly austere: this is a professional training
// system for people who already know the field, not a consumer app. No emoji
// appears anywhere in the product.

type GlyphProps = { size?: number; className?: string; fill?: string };

function frame(size: number, className: string | undefined, children: React.ReactNode) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={`studium-glyph${className ? ` ${className}` : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.55}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/* ---- Stage marks ------------------------------------------------------- */

/** Briefing: the Studium arch frames a first, deliberate step. */
export function GlyphBriefing({ size = 20, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M4.5 20V11a7.5 7.5 0 0 1 15 0v9" />
    <path d="M8.5 20v-3h3v-3h3v-3h2.5" />
    <circle cx="12" cy="8" r="1.4" fill="var(--brand-gold)" stroke="none" />
  </>);
}

/** Concepts: a nested arch and a fixed point inside the learning space. */
export function GlyphConcepts({ size = 20, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M4.5 20V11a7.5 7.5 0 0 1 15 0v9" />
    <path d="M8 20v-8.5a4 4 0 0 1 8 0V20" />
    <circle cx="12" cy="9.5" r="1.5" fill="var(--brand-gold)" stroke="none" />
  </>);
}

/** Worked example: the logo's rising staircase, one verified decision at a time. */
export function GlyphWorkedExample({ size = 20, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M3.5 20h17V5" />
    <path d="M5.5 18h4v-4h4v-4h4V6h3" />
    <circle cx="18" cy="5.7" r="1.35" fill="var(--brand-gold)" stroke="none" />
  </>);
}

/** Practice: the learner's work rises inside the same disciplined frame. */
export function GlyphPractice({ size = 20, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M4.5 20V11a7.5 7.5 0 0 1 15 0v9" />
    <path d="M8.5 20v-3h3v-3h3v-3h2.5" />
    <path d="M7.5 20h9" />
  </>);
}

/** Mastery gate: the arch opens only once the final step is complete. */
export function GlyphMastery({ size = 20, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M4.5 20V11a7.5 7.5 0 0 1 15 0v9" />
    <path d="m8.5 14.1 2.4 2.5 4.9-5.3" />
    <circle cx="12" cy="7.8" r="1.25" fill="var(--brand-gold)" stroke="none" />
  </>);
}

/** Portfolio: accumulated steps form a tangible professional record. */
export function GlyphPortfolio({ size = 20, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M4 20h16V5" />
    <path d="M5.5 18h4v-3.5h3.8V11h3.8V7.5H20" />
    <path d="M5.5 5.5h7" />
    <circle cx="16.8" cy="5.5" r="1.2" fill="var(--brand-gold)" stroke="none" />
  </>);
}

export const STAGE_GLYPHS: Record<string, (props: GlyphProps) => React.JSX.Element> = {
  briefing: GlyphBriefing,
  'concept-radar': GlyphConcepts,
  'worked-example': GlyphWorkedExample,
  'practice-arena': GlyphPractice,
  'mastery-gate': GlyphMastery,
  'learning-portfolio': GlyphPortfolio,
};

/* ---- Insignia ---------------------------------------------------------- */

/** Each insignia is a distinct silhouette so it is legible at badge size. */
export function GlyphFirstGate({ size = 22, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M5 20V9l7-5 7 5v11" />
    <path d="M12 20v-6" />
    <path d="M3 20h18" />
  </>);
}

export function GlyphCleanSweep({ size = 22, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M4 12.5l5 5L20 6.5" />
    <path d="M4 18.5h6" />
  </>);
}

export function GlyphWeekCloser({ size = 22, className }: GlyphProps) {
  return frame(size, className, <>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M3.5 9.5h17" />
    <path d="M8 3v4M16 3v4" />
    <path d="M8.5 14.5l2.5 2.5 4.5-4.5" />
  </>);
}

export function GlyphHalfCourse({ size = 22, className }: GlyphProps) {
  return frame(size, className, <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 3.5a8.5 8.5 0 0 1 0 17Z" fill="currentColor" stroke="none" opacity="0.85" />
  </>);
}

export function GlyphLabBuilder({ size = 22, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M9.5 3v6.2L4.6 18a2 2 0 0 0 1.7 3h11.4a2 2 0 0 0 1.7-3l-4.9-8.8V3" />
    <path d="M8 3h8" />
    <path d="M7.6 14.5h8.8" />
  </>);
}

export function GlyphSecurityCleared({ size = 22, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M12 3l7.5 3v6c0 4.6-3.1 8-7.5 9.5C7.6 20 4.5 16.6 4.5 12V6L12 3Z" />
    <path d="M8.8 12.2l2.3 2.3 4.1-4.6" />
  </>);
}

export function GlyphStreakSeven({ size = 22, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M12 3c3.2 3.4 5.6 6 5.6 9.2A5.6 5.6 0 0 1 12 21a5.6 5.6 0 0 1-5.6-8.8C6.4 9 8.8 6.4 12 3Z" />
    <path d="M12 21a2.8 2.8 0 0 0 2.8-4.4c0-1.6-1.2-2.9-2.8-4.6" />
  </>);
}

export function GlyphFullCommand({ size = 22, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M12 2.5l2.7 5.9 6.4.8-4.7 4.4 1.2 6.4L12 16.9 6.4 20l1.2-6.4L2.9 9.2l6.4-.8L12 2.5Z" />
  </>);
}

export const INSIGNIA_GLYPHS: Record<string, (props: GlyphProps) => React.JSX.Element> = {
  'first-gate': GlyphFirstGate,
  'clean-sweep': GlyphCleanSweep,
  'week-closer': GlyphWeekCloser,
  'half-course': GlyphHalfCourse,
  'lab-builder': GlyphLabBuilder,
  'security-cleared': GlyphSecurityCleared,
  'streak-seven': GlyphStreakSeven,
  'full-command': GlyphFullCommand,
};

/* ---- Editorial marks --------------------------------------------------- */

/** Marks the "what to notice" note that sits under a code sample. */
export function GlyphReadThis({ size = 18, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="2.6" />
  </>);
}

/** Marks a failure mode: the signal that exposes it. */
export function GlyphFailureMode({ size = 18, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M12 3.5 21 19.5H3L12 3.5Z" />
    <path d="M12 10v4" />
    <path d="M12 17h.01" />
  </>);
}

/** Marks a trade-off: two things genuinely in tension. */
export function GlyphTradeOff({ size = 18, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M12 3.5v17" />
    <path d="M5 8h14" />
    <path d="M5 8l-2.5 5.5h5L5 8Z" />
    <path d="M19 8l-2.5 5.5h5L19 8Z" />
  </>);
}

/** Marks the decision this concept puts in the reader's hands. */
export function GlyphPmLens({ size = 18, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M3.5 6.5h17v11h-17z" />
    <path d="M8.5 6.5V4.5h7v2" />
    <path d="M9.5 12h5" />
  </>);
}

/** Marks the retrieval drill. */
export function GlyphDrill({ size = 18, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M4 20l3.5-1 10-10a2.1 2.1 0 0 0-3-3l-10 10L4 20Z" />
    <path d="M14 6.5l3.5 3.5" />
  </>);
}

/** Marks a pitfall entry. */
export function GlyphPitfall({ size = 18, className }: GlyphProps) {
  return frame(size, className, <>
    <path d="M3 8c2.5 3 4 6.5 4 12" />
    <path d="M21 8c-2.5 3-4 6.5-4 12" />
    <path d="M7 20h10" />
    <path d="M12 3v8" />
  </>);
}

/* ---- Studium interface alphabet --------------------------------------- */

/**
 * The product never falls back to a vendor icon library. These marks share
 * the academy's gate / route / evidence geometry, so even small controls read
 * as part of Studium rather than a generic dashboard.
 */
function systemGlyph(kind: string, { size = 20, className, fill }: GlyphProps) {
  const path = (() => {
    switch (kind) {
      case 'activity': return <><path d="M3 12h4l2.1-5.3 4.2 10.6 2.2-5.3H21" /><circle cx="3" cy="12" r="1" /><circle cx="21" cy="12" r="1" /></>;
      case 'arrow-left': return <><path d="M20 12H5" /><path d="m10 7-5 5 5 5" /><circle cx="20" cy="12" r="1.3" /></>;
      case 'book': return <><path d="M4 5.5c2.7-1.4 5.5-1.1 8 1v12c-2.5-2.1-5.3-2.4-8-1Z" /><path d="M20 5.5c-2.7-1.4-5.5-1.1-8 1v12c2.5-2.1 5.3-2.4 8-1Z" /><path d="M12 6.5v12" /></>;
      case 'bot': return <><rect x="4" y="6" width="16" height="13" rx="4" /><path d="M12 3v3M9 12h.01M15 12h.01M8.5 15.5h7" /><circle cx="12" cy="3" r="1" /></>;
      case 'brain': return <><path d="M9 5.2A3.1 3.1 0 0 0 4.2 8a3.2 3.2 0 0 0 .6 6.1A3.6 3.6 0 0 0 9 19.5h1.1V5.2Z" /><path d="M15 5.2A3.1 3.1 0 0 1 19.8 8a3.2 3.2 0 0 1-.6 6.1 3.6 3.6 0 0 1-4.2 5.4h-1.1V5.2Z" /><path d="M9 9h3m3 0h-3m0 4h3M12 5.2v14.3" /><circle cx="12" cy="9" r="1" /><circle cx="12" cy="13" r="1" /></>;
      case 'check': return <path d="m4 12.5 4.8 4.7L20 6.3" />;
      case 'check-circle': return <><circle cx="12" cy="12" r="8.8" /><path d="m7.7 12.2 2.9 2.9 5.7-6.2" /></>;
      case 'chevron-left': return <path d="m15.5 5-7 7 7 7" />;
      case 'chevrons-left': return <><path d="m18 5-7 7 7 7" /><path d="m11 5-7 7 7 7" /></>;
      case 'chevrons-right': return <><path d="m6 5 7 7-7 7" /><path d="m13 5 7 7-7 7" /></>;
      case 'clipboard': return <><rect x="5" y="5.3" width="14" height="15" rx="2.5" /><path d="M9 5.3V4a3 3 0 0 1 6 0v1.3M8.5 11h7M8.5 15h5" /></>;
      case 'cpu': return <><rect x="7" y="7" width="10" height="10" rx="2" /><path d="M9.5 3v4M14.5 3v4M9.5 17v4M14.5 17v4M3 9.5h4M3 14.5h4M17 9.5h4M17 14.5h4" /><circle cx="12" cy="12" r="2.1" /></>;
      case 'file': return <><path d="M7 3.5h7l4 4v13H7a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" /><path d="M14 3.5v4h4M8.5 12h7M8.5 16h5" /></>;
      case 'graduate': return <><path d="m3 9 9-4 9 4-9 4Z" /><path d="M6.5 11v4.3c2.8 2.3 8.2 2.3 11 0V11" /><path d="M21 9v6" /><circle cx="21" cy="16.5" r="1" /></>;
      case 'layers': return <><path d="m4 8 8-4 8 4-8 4Z" /><path d="m4 12 8 4 8-4M4 16l8 4 8-4" /></>;
      case 'dashboard': return <><rect x="3.5" y="4" width="17" height="16" rx="3" /><path d="M8 4v16M8 10h12M12 15.5h4" /><circle cx="5.8" cy="7" r=".8" /></>;
      case 'key': return <><circle cx="8" cy="12" r="4" /><path d="M12 12h9M17 12v3M20 12v2" /><circle cx="8" cy="12" r="1" /></>;
      case 'logout': return <><path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5" /><path d="M13 8l4 4-4 4M17 12H8" /></>;
      case 'message': return <><path d="M5 5.5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-8l-5.5 3v-13a2 2 0 0 1 2-2Z" /><path d="M9 10h6M9 13.5h4" /></>;
      case 'refresh': return <><path d="M19 8.5A7.7 7.7 0 1 0 20 14" /><path d="M19 4.5v4h-4" /><circle cx="12" cy="12" r="1" /></>;
      case 'reset': return <><path d="M7 7.5H3.5V4" /><path d="M4 7.5A8.4 8.4 0 1 1 4.5 17" /><path d="M12 8.5v4l2.8 1.8" /></>;
      case 'save': return <><path d="M5 4h12l3 3v13H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" /><path d="M7 4v6h9V4M8 20v-6h8v6" /></>;
      case 'send': return <><path d="m3.5 4 17 8-17 8 3.2-8Z" /><path d="M6.7 12h13.8" /></>;
      case 'settings': return <><circle cx="12" cy="12" r="3" /><path d="M12 3.5v2M12 18.5v2M20.5 12h-2M5.5 12h-2M18 6l-1.4 1.4M7.4 16.6 6 18M18 18l-1.4-1.4M7.4 7.4 6 6" /><circle cx="12" cy="12" r="7.3" /></>;
      case 'shield': return <><path d="M12 3 19 6v5.8c0 4.5-2.9 7.6-7 9.2-4.1-1.6-7-4.7-7-9.2V6Z" /><path d="m8.7 12.1 2.2 2.3 4.5-4.8" /></>;
      case 'spark': return <><path d="m12 2.8 1.5 6.1 5.7 3.1-5.7 3.1-1.5 6.1-1.5-6.1-5.7-3.1 5.7-3.1Z" /><path d="m19 3 .5 2 .5.5-2 .5-.5 2-.5-2-2-.5 2-.5Z" /></>;
      case 'target': return <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.2" /><circle cx="12" cy="12" r="1" /><path d="M12 3.5V2M12 22v-1.5M3.5 12H2M22 12h-1.5" /></>;
      case 'user-check': return <><circle cx="9" cy="8" r="3.4" /><path d="M3.5 20c.7-4 2.5-6 5.5-6s4.8 2 5.5 6M16 15.5l1.7 1.7L21 13.7" /></>;
      case 'users': return <><circle cx="9" cy="8.5" r="3" /><path d="M3.5 20c.7-3.6 2.5-5.5 5.5-5.5s4.8 1.9 5.5 5.5" /><path d="M16 6.5a2.8 2.8 0 0 1 0 5.4M16.3 14.7c2.3.2 3.6 1.9 4.2 5" /></>;
      case 'x-circle': return <><circle cx="12" cy="12" r="8.7" /><path d="m8.7 8.7 6.6 6.6m0-6.6-6.6 6.6" /></>;
      case 'circle-dot': return <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2.2" /></>;
      case 'flag': return <><path d="M6 21V4.2" /><path d="M6 5c3.8-2.1 6.2 2 11.5 0v8.2C12.2 15.3 9.8 11.2 6 13.3" /></>;
      case 'play': return <path d="m8.5 5.5 9.5 6.5-9.5 6.5Z" fill={fill ?? 'currentColor'} />;
      case 'search': return <><circle cx="10.5" cy="10.5" r="5.8" /><path d="m15 15 5 5" /><circle cx="10.5" cy="10.5" r="1" /></>;
      case 'loader': return <><path d="M12 3.2a8.8 8.8 0 1 0 8.1 5.3" /><path d="M12 3.2v4.2" /><circle cx="20.1" cy="8.5" r="1.4" fill="currentColor" stroke="none" /></>;
      default: return <circle cx="12" cy="12" r="8" />;
    }
  })();
  return frame(size, className, path);
}

export const Activity = (props: GlyphProps) => systemGlyph('activity', props);
export const ArrowLeft = (props: GlyphProps) => systemGlyph('arrow-left', props);
export const BookOpen = (props: GlyphProps) => systemGlyph('book', props);
export const Bot = (props: GlyphProps) => systemGlyph('bot', props);
export const BrainCircuit = (props: GlyphProps) => systemGlyph('brain', props);
export const Check = (props: GlyphProps) => systemGlyph('check', props);
export const CheckCircle2 = (props: GlyphProps) => systemGlyph('check-circle', props);
export const ChevronLeft = (props: GlyphProps) => systemGlyph('chevron-left', props);
export const ChevronsLeft = (props: GlyphProps) => systemGlyph('chevrons-left', props);
export const ChevronsRight = (props: GlyphProps) => systemGlyph('chevrons-right', props);
export const CircleDot = (props: GlyphProps) => systemGlyph('circle-dot', props);
export const ClipboardCheck = (props: GlyphProps) => systemGlyph('clipboard', props);
export const Cpu = (props: GlyphProps) => systemGlyph('cpu', props);
export const FileText = (props: GlyphProps) => systemGlyph('file', props);
export const Flag = (props: GlyphProps) => systemGlyph('flag', props);
export const GraduationCap = (props: GlyphProps) => systemGlyph('graduate', props);
export const KeyRound = (props: GlyphProps) => systemGlyph('key', props);
export const Layers3 = (props: GlyphProps) => systemGlyph('layers', props);
export const LayoutDashboard = (props: GlyphProps) => systemGlyph('dashboard', props);
export const LoaderCircle = (props: GlyphProps) => systemGlyph('loader', props);
export const LogOut = (props: GlyphProps) => systemGlyph('logout', props);
export const MessageSquareText = (props: GlyphProps) => systemGlyph('message', props);
export const Play = (props: GlyphProps) => systemGlyph('play', props);
export const RefreshCw = (props: GlyphProps) => systemGlyph('refresh', props);
export const RotateCcw = (props: GlyphProps) => systemGlyph('reset', props);
export const Save = (props: GlyphProps) => systemGlyph('save', props);
export const Search = (props: GlyphProps) => systemGlyph('search', props);
export const Send = (props: GlyphProps) => systemGlyph('send', props);
export const Settings2 = (props: GlyphProps) => systemGlyph('settings', props);
export const ShieldCheck = (props: GlyphProps) => systemGlyph('shield', props);
export const Sparkles = (props: GlyphProps) => systemGlyph('spark', props);
export const Target = (props: GlyphProps) => systemGlyph('target', props);
export const UserCheck = (props: GlyphProps) => systemGlyph('user-check', props);
export const UsersRound = (props: GlyphProps) => systemGlyph('users', props);
export const XCircle = (props: GlyphProps) => systemGlyph('x-circle', props);

/** The official Studium doorway-and-stair mark, cropped directly from the approved logo. */
export function StudiumEmblem({ size = 40, className }: GlyphProps) {
  return <img className={className} src="/brand/studium-symbol.png" width={size} height={size} alt="" aria-hidden="true" />;
}

/** Full approved lockup, including the wordmark and official slogan. */
export function StudiumLogo({ className, alt = 'STUDIUM — learn.practice.lead' }: Pick<GlyphProps, 'className'> & { alt?: string }) {
  return <img className={className} src="/brand/studium-logo.png" alt={alt} />;
}

/** Compact approved wordmark for navigation, workspace and footer use. */
export function StudiumWordmark({ className, alt = 'STUDIUM — learn.practice.lead' }: Pick<GlyphProps, 'className'> & { alt?: string }) {
  return <img className={className} src="/brand/studium-wordmark.png" alt={alt} />;
}

/** The public-header lockup uses the official letterforms without the tagline. */
export function StudiumHeaderWordmark({ className, alt = 'STUDIUM' }: Pick<GlyphProps, 'className'> & { alt?: string }) {
  return <img className={className} src="/brand/studium-header-wordmark.png" alt={alt} />;
}
